import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Calculator, 
  Shield, 
  Clock, 
  CheckCircle,
  ChevronRight,
  Car,
  Percent,
  Users
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import HeroQuickStart from './landing/HeroQuickStart';
import { INDICATIVE_RATE, INDICATIVE_RATE_PCT, DEFAULT_TERM_MONTHS, weeklyRepayment } from '../lib/finance';
import { unsplash } from '../lib/images';

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;

// The advertised indicative rate now lives in lib/finance, shared with the /calculator page so
// the two can't quote different numbers.
const HERO_RATE = INDICATIVE_RATE;

// Lenders section imagery. Unsplash, matching the pattern already used for the hero — swap for a
// real Levr photo when there is one.
const LENDERS_IMAGE = unsplash('photo-1592275142354-0d765e9a9c42', { w: 1400 });

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', maximumFractionDigits: 0 }).format(amount);
};

const Landing = () => {
  const [vehiclePrice, setVehiclePrice] = useState(35000);
  const [deposit, setDeposit] = useState(5000);
  const navigate = useNavigate();

  const goToFunnel = () => navigate('/get-started');

  const loanAmount = vehiclePrice - deposit;
  const estimatedWeekly = weeklyRepayment(loanAmount, DEFAULT_TERM_MONTHS, HERO_RATE);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0 bg-black" />
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url("${unsplash('photo-1747658507367-5d1ff1e1c6cb', { w: 1920, q: 60 })}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Not "No Credit Check Required": as a blanket hero claim it reads as "getting
                  finance from Levr involves no credit check", which isn't true — only the
                  eligibility check itself is credit-check free, and the paragraph below says so
                  in that narrower context where it is accurate. */}
              <span className="inline-flex items-center gap-2 text-[#00D47E] text-sm font-medium mb-6">
                <Clock className="w-4 h-4" />
                Quick Approvals, Great Rates
              </span>
              
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                See How Much
                <span className="text-[#00D47E]"> You Can Get</span>
                <span className="block text-2xl sm:text-3xl lg:text-4xl font-medium text-gray-300 mt-2">in less than 2 minutes</span>
              </h1>
              
              <p className="text-gray-300 text-lg md:text-xl max-w-lg mb-8 leading-relaxed">
                Find out your approval likelihood and indicative repayments in less than 2 minutes. No credit check, no impact on your credit score.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="hero-cta-btn"
                  onClick={goToFunnel}
                  className="bg-[#00D47E] text-black border-2 border-[#00D47E] hover:bg-transparent hover:text-white hover:border-white rounded-full px-8 py-6 text-lg font-medium transition-all"
                >
                  Check My Eligibility
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  data-testid="hero-learn-more-btn"
                  variant="outline"
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-2 border-white/30 text-white hover:bg-[#00D47E] hover:text-black hover:border-[#00D47E] rounded-full px-8 py-6 text-lg transition-all"
                >
                  Learn More
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="flex gap-8 mt-10 pt-8 border-t border-gray-700">
                <div>
                  <p className="font-outfit text-3xl font-bold text-white">2 min</p>
                  <p className="text-gray-400 text-sm">Quick check</p>
                </div>
                <div>
                  <p className="font-outfit text-3xl font-bold text-white">No</p>
                  <p className="text-gray-400 text-sm">Credit impact</p>
                </div>
                {/* This slot used to read "12+ / NZ Lenders". Removed on the owner's instruction
                    (2026-08-26): "we don't use all those lenders so no need to mention them." The
                    number came from a seeded table that included two products that are not lenders
                    at all, and Google's AI Overview had begun repeating it to searchers as fact.
                    "Free / No obligation" is claimed elsewhere on the site already and needs no
                    panel to stand behind it. Do not reinstate a lender count without a confirmed
                    accredited panel — see scripts/static-routes.js for the same rule on /compare. */}
                <div>
                  <p className="font-outfit text-3xl font-bold text-white">Free</p>
                  <p className="text-gray-400 text-sm">No obligation</p>
                </div>
              </div>
            </motion.div>

            {/* Hero quick-start: calculator + a 3-step capture, no assessment. Rendered ONCE —
                the grid stacks it under the copy on mobile. The full 6-step eligibility flow now
                lives in its own section further down. */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <HeroQuickStart rate={HERO_RATE} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section data-testid="features-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-outfit text-3xl md:text-4xl font-bold text-black mb-4"
            >
              Why Choose Levr?
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-gray-600 text-lg max-w-2xl mx-auto"
            >
              We make finding the best vehicle finance deal simple, fast, and stress-free.
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: Percent,
                // No guarantees and no superlatives here — our own Terms say we don't guarantee
                // any specific rate or outcome, so the marketing can't claim otherwise.
                title: 'Low Rates, We Got You',
                // "our panel of NZ lenders" removed 2026-08-26 — Levr has no confirmed accredited
                // panel, so the claim can't be made. What a specialist actually does is argue the
                // applicant's case, which is true and needs nothing to substantiate it.
                description: 'A specialist works your application to find the sharpest rate you qualify for.'
              },
              {
                icon: Clock,
                title: 'Quick Approval',
                description: 'Get indicative approval in minutes, not days. Start driving sooner.'
              },
              {
                icon: Users,
                title: 'Expert Support',
                description: 'Our team guides you through the entire process from quote to keys.'
              }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                data-testid={`feature-card-${idx}`}
                className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-14 h-14 bg-[#00D47E]/10 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-[#00D47E]" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-black mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Full eligibility check — the calculator plus the way in to the 6-step assessment.
          The hero captures a contactable lead fast; this is for people who want the score. */}
      <section id="check-eligibility" data-testid="eligibility-section" className="py-20 md:py-28 bg-black">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-10"
          >
            <motion.span variants={fadeInUp} className="inline-flex items-center gap-2 text-[#00D47E] text-sm font-medium mb-5">
              <Shield className="w-4 h-4" />
              No credit check, no impact on your score
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">
              Want to see your chances of being approved without getting credit checked?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-400 text-lg max-w-2xl mx-auto">
              Set your numbers below, then answer a few questions about your situation. You&apos;ll
              get an indicative approval likelihood in about two minutes.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-white/20"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between text-sm text-gray-400 mb-2">
                    <span>Vehicle Price</span>
                    <span className="text-white font-medium">{formatCurrency(vehiclePrice)}</span>
                  </div>
                  <input
                    type="range" min="5000" max="350000" step="1000" value={vehiclePrice}
                    aria-label="Vehicle price"
                    data-testid="eligibility-price"
                    onChange={(e) => setVehiclePrice(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-[#00D47E] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1"><span>$5k</span><span>$350k</span></div>
                </div>

                <div>
                  <div className="flex justify-between text-sm text-gray-400 mb-2">
                    <span>Deposit</span>
                    <span className="text-white font-medium">{formatCurrency(deposit)}</span>
                  </div>
                  <input
                    type="range" min="0" max={vehiclePrice * 0.5} step="500" value={deposit}
                    aria-label="Deposit"
                    data-testid="eligibility-deposit"
                    onChange={(e) => setDeposit(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-[#00D47E] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$0</span><span>{formatCurrency(vehiclePrice * 0.5)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-black/40 rounded-xl p-6">
                <div className="flex justify-between items-baseline mb-3">
                  <p className="text-sm text-gray-400">Loan Amount</p>
                  <p className="font-outfit text-2xl font-bold text-[#00D47E]">{formatCurrency(loanAmount)}</p>
                </div>
                <div className="border-t border-white/10 pt-3">
                  <p className="text-sm text-gray-400">Estimated Weekly</p>
                  <p className="font-outfit text-4xl font-bold text-white">{formatCurrency(estimatedWeekly)}</p>
                  <p className="text-xs text-gray-500 mt-1">Based on {INDICATIVE_RATE_PCT}% over {DEFAULT_TERM_MONTHS} months</p>
                </div>
                <Button
                  data-testid="eligibility-cta-btn"
                  onClick={goToFunnel}
                  className="w-full mt-6 bg-[#00D47E] text-black hover:bg-[#00D47E]/90 rounded-full py-6 text-base font-semibold"
                >
                  Check my chances
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-center text-xs text-gray-500 mt-3">
                  Takes about 2 minutes · no credit check
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lenders Section — text left, image right.
          Deliberately names no lenders and quotes no rates: advertising a panel and "from X%"
          invites both a compliance problem and a promise we can't keep for every applicant. */}
      <section data-testid="lenders-section" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            {/* Copy */}
            <div>
              <motion.h2
                variants={fadeInUp}
                className="font-outfit text-3xl md:text-4xl font-bold text-black mb-5"
              >
                Lenders we&apos;d use ourselves
              </motion.h2>

              <motion.p variants={fadeInUp} className="text-gray-600 text-lg leading-relaxed mb-4">
                Levr works with established New Zealand banks and finance companies — providers with
                a real track record, not short-term credit chasing high-margin debt. They operate
                under New Zealand&apos;s responsible lending rules, and we hold them to the same
                standard we hold ourselves.
              </motion.p>

              <motion.p variants={fadeInUp} className="text-gray-600 text-lg leading-relaxed mb-8">
                That means clear terms you can actually read, rates that reflect your situation, and
                nobody pushing you to borrow more than you should. If a lender wouldn&apos;t treat
                you fairly, they don&apos;t see your application.
              </motion.p>

              <motion.ul variants={fadeInUp} className="space-y-3">
                {[
                  // "before they join our panel" removed 2026-08-26 — no confirmed panel to join.
                  'Established New Zealand lenders, not whoever pays the most',
                  'Responsible lending standards, applied to every application',
                  'A specialist matches you — no automated hard sell',
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#00D47E] shrink-0 mt-0.5" />
                    <span className="text-gray-700">{point}</span>
                  </li>
                ))}
              </motion.ul>
            </div>

            {/* Visual */}
            <motion.div variants={fadeInUp} className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-black shadow-2xl">
                <img
                  src={LENDERS_IMAGE}
                  alt="An open road curving alongside a lake beneath New Zealand mountains at sunrise"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -left-5 hidden sm:block bg-white rounded-xl shadow-xl border border-gray-100 px-5 py-4">
                <p className="font-outfit text-2xl font-bold text-black">Real people</p>
                <p className="text-sm text-gray-500">reviewing every application</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" data-testid="how-it-works-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-outfit text-3xl md:text-4xl font-bold text-black mb-4"
            >
              How It Works
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-gray-600 text-lg"
            >
              Three simple steps to your new vehicle
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              { step: '01', title: 'Tell Us About You', desc: 'Answer a few quick questions about your income and situation' },
              { step: '02', title: 'See Your Result', desc: 'Get your approval likelihood and indicative repayments instantly' },
              { step: '03', title: 'We Get In Touch', desc: 'A finance specialist reviews your details and calls to talk through your options' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                data-testid={`step-${idx + 1}`}
                className="relative"
              >
                <div className="flex items-start gap-4">
                  <span className="font-outfit text-6xl font-bold text-[#00D47E]/20">
                    {item.step}
                  </span>
                  <div className="pt-4">
                    <h3 className="font-outfit text-xl font-semibold text-black mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                </div>
                {idx < 2 && (
                  <div className="hidden md:block absolute top-8 right-0 w-16 h-px bg-gray-200" />
                )}
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button
              data-testid="how-it-works-cta"
              onClick={goToFunnel}
              className="bg-black text-white hover:bg-[#00D47E] hover:text-black rounded-full px-8 py-6 text-lg transition-all"
            >
              Check My Eligibility
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
